// src/components/Topbar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUser,
  FiLogOut,
  FiChevronDown,
  FiMail,
  FiCheckCircle,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  getProfile,
  sendVerify,
  logout as logoutApi,
  isJustLoggedOut,
  clearJustLoggedOut,
} from '../services/userService';

// NEW: billing status helpers + hook
import { usePlan } from '../hooks/useBillingQueries';
import {
  isSubscriptionReadOnly,
  isSubscriptionWarning,
} from '../services/billingService';

/* ----------------------- helpers ----------------------- */
function parseRetryAfter(header) {
  if (!header) return 0;
  const n = Number(header);
  if (!Number.isNaN(n) && n > 0) return Math.min(n, 120);
  const t = Date.parse(header);
  if (!Number.isNaN(t)) {
    const secs = Math.ceil((t - Date.now()) / 1000);
    return Math.min(Math.max(secs, 0), 120);
  }
  return 0;
}
function initialsFrom(str) {
  if (!str) return 'U';
  const parts = String(str).trim().split(/\s+/);
  if (parts.length === 1) {
    const s = parts[0];
    const base = s.includes('@') ? s.split('@')[0] : s;
    return (base[0] || 'U').toUpperCase();
  }
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/* ----------------------- component ----------------------- */
export default function Topbar() {
  const navigate = useNavigate();

  // menu
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // profile
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);

  // verify chip
  const isUnverified = useMemo(
    () => !!me && !(me.emailVerifiedAt || me.isVerified),
    [me]
  );
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // NEW: subscription status (for global banner)
  const { data: billingSummary } = usePlan({
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const billingStatusRaw =
    billingSummary?.billingStatus ||
    billingSummary?.stripe?.subscriptionStatus ||
    null;

  const billingStatus = billingStatusRaw
    ? String(billingStatusRaw).toLowerCase()
    : null;

  const subscriptionReadOnly = billingStatus
    ? isSubscriptionReadOnly(billingStatus)
    : false;
  const subscriptionWarning = billingStatus
    ? isSubscriptionWarning(billingStatus)
    : false;

  // compact env badge (override with REACT_APP_ENV_LABEL if present)
  const envBadge = useMemo(() => {
    const label = process.env.REACT_APP_ENV_LABEL;
    if (label) return label;
    const host = window.location.hostname;
    if (host === 'localhost' || host.endsWith('.local')) return 'DEV';
    if (host.includes('staging') || host.includes('preview')) return 'STAGING';
    return null;
  }, []);

  // click-outside / esc
  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // cooldown timer
  useEffect(() => {
    if (!resendCooldown) return;
    const id = setInterval(() => setResendCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // hydrate profile (cache -> fresh) + restore dismissal state
  useEffect(() => {
    // Hard guard: if we *just* logged out, skip any boot-time auth probe in this component.
    if (isJustLoggedOut()) {
      try { clearJustLoggedOut(); } catch {}
      setLoadingMe(false);
      setMe(null);
      return;
    }

    try {
      const cached = localStorage.getItem('me');
      if (cached) setMe(JSON.parse(cached));
    } catch {}

    (async () => {
      setLoadingMe(true);
      try {
        const fresh = await getProfile();
        setMe(fresh);
        try { localStorage.setItem('me', JSON.stringify(fresh)); } catch {}
        const key = `verifyBannerDismissed:${fresh.email || fresh.id}`;
        setBannerDismissed(localStorage.getItem(key) === '1');
      } catch {
        // PrivateRoute handles redirects; keep quiet here
      } finally {
        setLoadingMe(false);
      }
    })();
  }, []);

  // actions
  async function handleLogout() {
    setMenuOpen(false);
    try {
      await logoutApi(); // marks just-logged-out flag + clears server cookie + local caches
    } catch {
      // ignore network errors; we'll still force a stateless redirect next
    } finally {
      // Hard redirect to reset SPA state and prevent any interceptor from reviving session
      window.location.replace('/login');
    }
  }

  async function resendVerification() {
    if (!me?.email || resendLoading || resendCooldown > 0) return;
    setResendLoading(true);
    try {
      await sendVerify({ email: me.email });
      toast.success('Verification email sent.');
      setResendCooldown(30);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        const ra = parseRetryAfter(err?.response?.headers?.['retry-after']);
        setResendCooldown(ra || 60);
        toast.warning(
          ra
            ? `Too many requests — try again in ~${ra}s.`
            : 'Too many requests — try again later.'
        );
      } else {
        toast.error('Could not send verification email right now.');
      }
    } finally {
      setResendLoading(false);
    }
  }

  function dismissBanner() {
    setBannerDismissed(true);
    try {
      const key = `verifyBannerDismissed:${me?.email || me?.id || 'unknown'}`;
      localStorage.setItem(key, '1');
    } catch {}
  }

  const showVerifyChip = isUnverified && !bannerDismissed;

  /* ----------------------- UI ----------------------- */
  return (
    <div className="bg-white border-b px-3 md:px-4 py-2 space-y-2 relative z-40">
      {/* Subscription warning / read-only banners (no buttons, just guidance) */}
      {subscriptionWarning && !subscriptionReadOnly && (
        <div className="w-full bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
          Your subscription is past due. Some features may stop working if payment
          isn&apos;t updated. Update your payment details from{' '}
          <span className="font-semibold">Settings → Billing &amp; Subscription</span>.
        </div>
      )}

      {subscriptionReadOnly && (
        <div className="w-full bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-xs text-rose-800">
          Your subscription is inactive. The workspace is now read-only until billing
          is updated. Reactivate your subscription from{' '}
          <span className="font-semibold">Settings → Billing &amp; Subscription</span>.
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        {/* Inline verify chip (compact) */}
        {showVerifyChip && (
          <div className="hidden sm:flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-800">
            <FiMail className="text-indigo-600" />
            <span className="whitespace-nowrap">Verify your email</span>
            <button
              onClick={resendVerification}
              disabled={resendLoading || resendCooldown > 0}
              className="underline decoration-indigo-500/60 underline-offset-2 hover:opacity-90 disabled:opacity-60"
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : resendLoading
                ? 'Sending…'
                : 'Resend'}
            </button>
            <button
              onClick={dismissBanner}
              aria-label="Dismiss verification notice"
              className="ml-0.5 p-1 rounded-full hover:bg-indigo-100 text-indigo-700"
            >
              <FiX size={14} />
            </button>
          </div>
        )}

        {/* Environment badge */}
        {envBadge && (
          <span className="hidden md:inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-700 border border-gray-200">
            {envBadge}
          </span>
        )}

        {/* Account dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((s) => !s)}
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 px-2.5 py-1.5 transition-colors"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <div className="h-7 w-7 rounded-full bg-gray-800 text-white grid place-items-center text-xs font-bold">
              {initialsFrom(me?.name || me?.email)}
            </div>
            <div className="hidden sm:flex flex-col items-start leading-tight">
              <span className="text-sm font-medium text-gray-900">
                {loadingMe ? '—' : me?.name || me?.email || 'Account'}
              </span>
              <span className="text-[10px] text-gray-500">
                {loadingMe ? '' : me?.role ? me.role.toUpperCase() : ''}
                {!loadingMe && (me?.emailVerifiedAt || me?.isVerified) && (
                  <span className="inline-flex items-center gap-1 ml-1 text-emerald-600">
                    <FiCheckCircle /> Verified
                  </span>
                )}
              </span>
            </div>
            <FiChevronDown className="text-gray-600" size={16} />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
            >
              <div className="px-4 py-2.5 border-b">
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {me?.name || me?.email || 'My Account'}
                </div>
                {me?.email && (
                  <div className="text-xs text-gray-500 truncate">{me.email}</div>
                )}
              </div>

              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/profile');
                }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50"
                role="menuitem"
              >
                <FiUser className="text-gray-600" /> Profile
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-gray-50"
                role="menuitem"
              >
                <FiLogOut /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
